import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useCountBuyerEventsByStatusQuery, useGetContractStatisticsQuery } from "../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../auth/useLoggedInWithOrgContextState";
import { type SubAccess, useSub } from "../subscription/useSub";
import type { AppRole } from "./navConfig";

export interface OwnedWork {
  sourcingEvents: boolean;
  contracts: boolean;
}

export const withOwnedWork = (sub: SubAccess, owned: OwnedWork): SubAccess => ({
  ...sub,
  canViewSourcingEvents: sub.canViewSourcingEvents || owned.sourcingEvents,
  canViewContracts: sub.canViewContracts || owned.contracts,
  viewsSharedContracts: sub.viewsSharedContracts && !owned.contracts,
});

export const useNavAccess = (): { access: SubAccess; owned: OwnedWork; resolving: boolean } => {
  const sub = useSub();
  const orgId = useLoggedInWithOrgContextState().selectedOrg.id;

  const lapsedSourcing = !sub.hasBuyerAccess && sub.viewsSourcingEvents;
  const lapsedContracts = !sub.hasContractAccess && sub.viewsContracts;
  const { data: eventCounts, isError: eventCountFailed } = useCountBuyerEventsByStatusQuery(
    lapsedSourcing ? { orgId } : skipToken,
  );
  const { data: contractStatistics, isError: contractStatisticsFailed } = useGetContractStatisticsQuery(
    lapsedContracts ? { orgId, organizationId: orgId } : skipToken,
  );

  const owned: OwnedWork = {
    sourcingEvents:
      lapsedSourcing && (eventCountFailed || (eventCounts?.counts ?? []).some(({ count }) => count > 0)),
    contracts: lapsedContracts && (contractStatisticsFailed || (contractStatistics?.numberOfContracts ?? 0) > 0),
  };
  const resolving =
    (lapsedSourcing && eventCounts === undefined && !eventCountFailed) ||
    (lapsedContracts && contractStatistics === undefined && !contractStatisticsFailed);
  return { access: withOwnedWork(sub, owned), owned, resolving };
};

export const navRoleFor = (role: AppRole, owned: OwnedWork): AppRole =>
  role === "supplier" && owned.sourcingEvents ? "orderer" : role;
